/**
 * LocalizedMonetaryInput - Currency input with 2 decimal places
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useUserPreferences } from '../context';
import { useState, useCallback, useEffect } from 'react';

export interface LocalizedMonetaryInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  currency?: string;
  currencyPosition?: 'start' | 'end';
  allowNegative?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  id?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  min?: number;
  max?: number;
  autoFocus?: boolean;
}

export function LocalizedMonetaryInput({
  value,
  onChange,
  currency,
  currencyPosition = 'start',
  allowNegative = false,
  label,
  placeholder,
  helperText,
  error,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  id,
  onBlur: externalOnBlur,
  min: minProp,
  max,
  autoFocus,
}: LocalizedMonetaryInputProps) {
  const { formatNumber, parseNumber, formatOptions } = useUserPreferences();
  const generatedId = React.useId();

  const decimalSeparator = formatOptions.numberFormat === 'dot_comma' ? ',' : '.';
  const thousandSeparator = formatOptions.numberFormat === 'dot_comma' ? '.' : ',';
  const decimalPlaces = 2;
  const min = minProp !== undefined ? minProp : (allowNegative ? undefined : 0);

  // Format number for display
  const formatForDisplay = useCallback((num: number | null): string => {
    if (num === null || num === undefined) return '';

    return formatNumber(num, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  }, [formatNumber]);

  const [displayValue, setDisplayValue] = useState(() => formatForDisplay(value));
  const [isFocused, setIsFocused] = useState(false);

  // Update display when external value changes, but NOT while user is actively typing
  useEffect(() => {
    if (isFocused) return;
    const currentParsed = parseNumber(displayValue);
    if (value !== currentParsed) {
      setDisplayValue(formatForDisplay(value));
    }
  }, [value, parseNumber, formatForDisplay, displayValue, isFocused]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Allow empty input
    if (input === '') {
      setDisplayValue('');
      onChange(null);
      return;
    }

    // Build valid characters regex
    const validChars = allowNegative
      ? `[0-9${decimalSeparator}${thousandSeparator}\\-]`
      : `[0-9${decimalSeparator}${thousandSeparator}]`;
    const cleanedInput = input.replace(new RegExp(`[^${validChars.slice(1, -1)}]`, 'g'), '');

    // Prevent multiple decimal separators
    const decimalCount = (cleanedInput.match(new RegExp(`\\${decimalSeparator}`, 'g')) || []).length;
    if (decimalCount > 1) return;

    // Prevent multiple negatives or negative not at start
    if (allowNegative) {
      const negativeCount = (cleanedInput.match(/-/g) || []).length;
      if (negativeCount > 1) return;
      if (negativeCount === 1 && cleanedInput.indexOf('-') !== 0) return;
    }

    setDisplayValue(cleanedInput);

    // Parse and validate
    const parsed = parseNumber(cleanedInput);
    if (parsed !== null) {
      // Apply min/max constraints
      let constrained = parsed;
      if (min !== undefined && parsed < min) constrained = min;
      if (max !== undefined && parsed > max) constrained = max;
      onChange(constrained);
    }
  }, [allowNegative, decimalSeparator, thousandSeparator, min, max, onChange, parseNumber]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Format on blur for consistent display
    const parsed = parseNumber(displayValue);
    if (parsed !== null) {
      setDisplayValue(formatForDisplay(parsed));
    } else if (displayValue !== '') {
      setDisplayValue('');
      onChange(null);
    }

    externalOnBlur?.(e);
  }, [displayValue, parseNumber, formatForDisplay, onChange, externalOnBlur]);

  const inputId = id || `monetary-input-${generatedId}`;

  const showCurrencyStart = currency && currencyPosition === 'start';
  const showCurrencyEnd = currency && currencyPosition === 'end';

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <Label htmlFor={inputId} className="mb-1.5 block">
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </Label>
      )}
      <div className="relative">
        {showCurrencyStart && (
          <span className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            {currency}
          </span>
        )}
        <Input
          id={inputId}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          inputMode="decimal"
          size={size}
          className={cn(
            showCurrencyStart && 'ps-10',
            showCurrencyEnd && 'pe-10',
            error && 'border-destructive focus-visible:ring-destructive'
          )}
        />
        {showCurrencyEnd && (
          <span className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            {currency}
          </span>
        )}
      </div>
      {helperText && (
        <p className={cn(
          'text-xs mt-1',
          error ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {helperText}
        </p>
      )}
    </div>
  );
}
